
import { AppMenuItem, Menu, setupSteps, Step, UseSteps } from 'shared/steps/steps';
import { BaseStepProps } from 'shared/steps/types';
import { Steps_ApplicationFragment, Steps_PortalFragment } from '../generated.graphql';
import { AppContextData, useAppContext } from './AppContext';


interface RentalStepsState {
  portal: Steps_PortalFragment
  application: Steps_ApplicationFragment
}


// USED EXTENSIVELY
export const slugs = {
  welcome: 'welcome',
  cart: 'cart',
  importantInfo: 'important',
  aboutMe: 'aboutme',
  altContact: 'altcontact',
  address: 'address',
  income: 'income',
  bankStatements: 'statements',
  additionalInfo: 'additional',
  payment: 'payment',
  paid: 'paid',
  agreement: 'agreement',
  summary: 'summary',
  signature: 'signature',
  finished: 'finished',
} as const;

type SlugKey = keyof typeof slugs;
export type Slug = typeof slugs[SlugKey]

export type RentalStep = Step<Slug>
export type RentalMenuItem = AppMenuItem<Slug>
export type RentalMenu = Menu<Slug>


export const {isSlug, ...rentalSteps} = setupSteps<Slug, RentalStepsState>({

  slugs: Object.values(slugs),

  mkLink: (slug, state) => `/${state.portal.slug}/${slug}`,

  // Returns the ordered steps of the application based on the state
  getAppSteps: ({application: app, portal}: RentalStepsState) => [
    // Only show the welcome step for Studio19 In-Store Apps
    ...app.inStore ? 
      [{slug: slugs.welcome}] : [],
    {slug: slugs.cart},
    {slug: slugs.importantInfo, enabled: app.validation.cart === null},
    {slug: slugs.aboutMe, enabled: app.importantInfoRead !== null},
    ...app.alternativeContactRequired
      ? [{slug: slugs.altContact, enabled: app.customer.email.length > 0}] : [],
    {slug: slugs.address, enabled: app.validation.customer === null || app.customer.altContactName.length > 0},
    {slug: slugs.income, enabled: app.address.residentialAddress !== null},
    // Only show bank statements for applications that require it (rental >= $50 at time of writing})
    ...app.bankStatementsRequired 
      ? [{slug: slugs.bankStatements, enabled: !!app.income.workBasis}] : [],
    {
      slug: slugs.additionalInfo,
      enabled: app.bankStatementsRequired ? app.validation.bankStatements === null : !!app.income.workBasis
    },
    {
      slug: slugs.payment,
      enabled: (app.bankStatementsRequired && !!app.bankStatements.result) || (!app.bankStatementsRequired && app.address.residentialAddress !== null)
    },
    {slug: slugs.agreement, enabled: app.validation.payment === null},
    {slug: slugs.summary, enabled: app.validation.agreement === null},
    {slug: slugs.signature},
    {slug: slugs.finished, enabled: app.dateSubmitted !== null || portal.myLastApplicationId !== null},
  ],

  desktopMenu: [
    {title: 'Welcome',  slugs: [slugs.welcome], hide: true, omit: state => !state.application.inStore},
    {title: 'My Cart',  slugs: [slugs.cart, slugs.importantInfo], hide: state => !state.application.importantInfoRead},
    {title: 'About Me', slugs: [slugs.aboutMe, slugs.altContact, slugs.address]},
    {title: 'Employment',   slugs: [slugs.income, slugs.bankStatements, slugs.additionalInfo]},
    {title: 'Payment',  slugs: [slugs.payment]},
    {title: 'Submit',   slugs: [slugs.agreement, slugs.summary, slugs.signature, slugs.finished], hide: state => !state.application || !!state.application.dateSubmitted},
  ],

  mobileMenu: [
    {title: 'Welcome',                slugs: [slugs.welcome], hide: true, omit: state => !state.application.inStore},
    {title: 'My Cart',                slugs: [slugs.cart, slugs.importantInfo], hide: state => !state.application.importantInfoRead},
    {title: 'About Me',               slugs: [slugs.aboutMe]},
    {title: 'Alternative Contact',    slugs: [slugs.altContact], omit: state => !state.application.alternativeContactRequired},
    {title: 'Current Address',        slugs: [slugs.address]},
    {title: 'Employment',              slugs: [slugs.income]},
    {title: 'Bank Statements',        slugs: [slugs.bankStatements], omit: state => !state.application.bankStatementsRequired},
    {title: 'Additional Information', slugs: [slugs.additionalInfo]},
    {title: 'Payment Type',           slugs: [slugs.payment]},
    {title: 'Review Agreement',       slugs: [slugs.agreement]},
    {title: 'Application Summary',    slugs: [slugs.summary]},
    {title: 'Sign & Submit',          slugs: [slugs.signature, slugs.finished], hide: state => !state.application || !!state.application.dateSubmitted},
  ],

});


interface UseRentalStepsConfig {
  slug?: Slug
}


const getState = (appContextData: AppContextData) => 
  ({application: appContextData.application, portal: appContextData.portal});

export type UseRentalSteps = UseSteps<Slug>;

// USED EXTENSIVELY
export const useRentalSteps = ({slug}: UseRentalStepsConfig): UseRentalSteps => {
  const app = useAppContext();
  return rentalSteps.getNextAndPrevSteps(getState(app), slug || null);
};

export const pathPattern = '/:portal/:slug([-a-z]+)';

// USED 3x
export const useMenu = (): Menu<Slug> | null => {
  const app = useAppContext();
  const menu = rentalSteps.useMenu(app ? getState(app) : null, pathPattern);
  return menu;
}


export type RentalStepProps = BaseStepProps<Slug>;
